import {
  createSuspenseQuery,
  createQuery
} from "../../utils/queryFactories.js";
import { api } from "../../utils/fetch.js";
import { z } from "zod";

// Merged schema from ViewResourcesSchema and PublicViewResourceSchema
export const ViewResourceSchema = z.object({
  // Shared fields (preferring stricter types from public where applicable)
  id: z.int(),
  type: z.string().min(1).nullish(), // from public
  display_name: z.string().nullable(), // from public
  category: z.string().min(1).nullish(), // from public
  status: z.string().min(1).nullish(), // from public
  tag: z.array(z.string().nullish()), // specific type from wf
  job_title: z.string().nullable(), // from public
  team: z.string().min(1).nullish(), // from public
  location: z.string().nullable(), // from public
  order: z.number().min(0).max(2147483647), // range from public
  icon: z.string().min(1).nullish(), // from public
  label: z.string().max(12).nullable(), // from public
  hide_agenda: z.boolean().nullish(), // nullable from public
  checklist_count: z.int().nullish(), // nullable from public
  checklist_status: z.int().nullish(), // nullable from public
  assigned_boardusers: z.array(z.string().nullish()), // specific type from wf
  reminder_state: z.string().min(1).nullish(), // from public
  has_contacts: z.boolean().nullish(), // nullable from public
  aging: z.string().min(1).nullish(), // from public
  contact_ids: z.array(z.int()).nullish(), // specific type from wf
  note_ids: z.array(z.int()).nullish(),
  _group_value: z.string().nullish(),
  _group_count: z.int().nullish(),

  // Fields only in original ViewResourcesSchema
  _group_field: z.string().nullish(),

  // Fields only in PublicViewResourceSchema
  icon_value: z.string().nullish()
});

const ViewResourcesResponseSchema = z.array(ViewResourceSchema);

// Add discriminated union for wf and public requests
const wfRequest = z.object({
  api: z.literal("wf"),
  id: z.string() // Original key for wf endpoint
});
const publicRequest = z.object({
  api: z.literal("public"),
  slug: z.string(),
  id: z.string() // Key for public endpoint
});
const ViewResourcesRequestSchema = z.discriminatedUnion("api", [
  wfRequest,
  publicRequest
]);
const ViewResourcesRequestVariables = z.union([
  wfRequest.omit({ api: true }),
  publicRequest.omit({ api: true })
]);

export type ViewResourcesRequest = z.infer<typeof ViewResourcesRequestSchema>;
export type ViewResourcesResponse = z.infer<typeof ViewResourcesResponseSchema>;

// URL mappings for wf and public APIs
const urls = {
  wf: "/views/:id/resources",
  public: "/public/:slug/boardviews/:id/resources"
};

export const useViewResources = createQuery<
  ViewResourcesResponse,
  ViewResourcesRequest,
  Error
>({
  queryKey: ["ViewResources"],
  staleTime: 1000 * 60,
  fetcher: async (variables) => {
    const { api: viewResourcesAPI, ...rest } = variables;
    return api<Omit<ViewResourcesRequest, "api">, ViewResourcesResponse>({
      method: "GET",
      url: urls[viewResourcesAPI],
      requestSchema: ViewResourcesRequestVariables,
      responseSchema: ViewResourcesResponseSchema,
      body: rest
    });
  }
});

export const useViewResourcesSuspense = createSuspenseQuery<
  ViewResourcesResponse,
  ViewResourcesRequest,
  Error
>({
  queryKey: ["ViewResources"],
  staleTime: 1000 * 60,
  fetcher: async (variables) => {
    // await new Promise((resolve) => setTimeout(resolve, 10000));
    const { api: viewResourcesAPI, ...rest } = variables;
    return api<Omit<ViewResourcesRequest, "api">, ViewResourcesResponse>({
      method: "GET",
      url: urls[viewResourcesAPI],
      requestSchema: ViewResourcesRequestVariables,
      responseSchema: ViewResourcesResponseSchema,
      body: rest
    });
  }
});

// Saved for an example of how to do pagination with React Query

// export const useGetViewResourcesPaginatedWithSuspense =
//   createSuspenseInfiniteQuery<
//     ViewResourcesPaginatedResponse & { params: ViewResourcesPaginatedRequest },
//     ViewResourcesPaginatedRequest,
//     Error
//   >({
//     getNextPageParam: (lastPage) => {
//       const { results, params, count } = lastPage;
//       const { offset } = params;
//       if (offset + results.length < count) {
//         return Math.min(count, offset + params.limit);
//       }
//       return null;
//     },
//     initialPageParam: 0,
//     queryKey: ["ViewResourcesPaginatedWithSuspense"],
//     staleTime: 1000 * 60,
//     fetcher: async (variables, { pageParam, meta }) => {
//       const result = await api<
//         ViewResourcesPaginatedRequest,
//         ViewResourcesPaginatedResponse
//       >({
//         method: "GET",
//         url: `/views/:shared_view_id/resources`,
//         requestSchema: GetViewResourcesPaginatedRequestSchema,
//         responseSchema: GetViewResourcesSchemaResponsePaginated,
//         body: { ...variables, offset: pageParam }
//       });
//       return {
//         ...result,
//         params: {
//           ...variables,
//           offset: pageParam
//         }
//       };
//     }
//   });
